import { useEffect, useRef, useState } from 'react';
import { getMediaType } from '../../utils';
import {
  preloadMedia,
  syncVideoTiming,
  performTransition,
  controlVideoPlayback,
} from '../../utils/mediaTransition';
import {
  AnimationWrapper,
  MediaElement,
  StaticImage,
} from './KlydoAnimation.styles';

/** `HTMLMediaElement.HAVE_METADATA` — duration known, so a seek will stick. */
const HAVE_METADATA = 1;

type KlydoAnimationProps =
  | {
      photoUrl: string | undefined;
      loopUrl?: never;
      isPaused?: never;
      previewFrameSeconds?: never;
      muted?: never;
      playSoundOnce?: never;
      replaySoundTrigger?: never;
    }
  | {
      loopUrl: string | undefined;
      photoUrl?: never;
      isPaused?: boolean;
      /**
       * The frame the artist chose to represent this klydo (TK-416). Only used
       * while PAUSED — a playing loop is not sitting on any one frame.
       */
      previewFrameSeconds?: number | null;
      muted?: boolean;
      playSoundOnce?: boolean;
      replaySoundTrigger?: number;
    };

const KlydoAnimation = ({
  loopUrl,
  photoUrl,
  isPaused,
  previewFrameSeconds,
  muted = true,
  playSoundOnce,
  replaySoundTrigger,
}: KlydoAnimationProps) => {
  const [currentUrl, setCurrentUrl] = useState<string | undefined>(loopUrl);
  const [nextUrl, setNextUrl] = useState<string | undefined>(undefined);
  const [showCurrent, setShowCurrent] = useState(true);
  const [showNext, setShowNext] = useState(false);

  const currentVideoRef = useRef<HTMLVideoElement>(null);
  const nextVideoRef = useRef<HTMLVideoElement>(null);
  const prevUrlRef = useRef<string | undefined>(loopUrl);
  const lastTimeRef = useRef(0);
  const [playOnceDone, setPlayOnceDone] = useState(false);

  // Sound comes from the video element itself, so it is always in sync with
  // the animation. A play-once sound is auto-muted after one full loop pass.
  const effectiveMuted = muted || (!!playSoundOnce && playOnceDone);

  // A new clip starts with fresh play-once tracking — stale wrap state from
  // the previous klydo would mute the new one before its sound is heard.
  useEffect(() => {
    setPlayOnceDone(false);
    lastTimeRef.current = 0;
  }, [currentUrl]);

  // Unmuting or replaying a play-once klydo restarts the loop, so the sound
  // plays exactly one pass from its beginning, in sync with the animation.
  useEffect(() => {
    if (!playSoundOnce || muted) return;
    setPlayOnceDone(false);
    lastTimeRef.current = 0;
    if (currentVideoRef.current) {
      currentVideoRef.current.currentTime = 0;
    }
  }, [playSoundOnce, muted, replaySoundTrigger]);

  // Auto-mute a play-once sound at the loop wrap. Mute the element directly:
  // waiting for the state round-trip would leak the start of the second pass.
  useEffect(() => {
    const video = currentVideoRef.current;
    if (!video || !playSoundOnce || effectiveMuted) return;

    const handleTimeUpdate = () => {
      if (video.currentTime < lastTimeRef.current) {
        video.muted = true;
        setPlayOnceDone(true);
      }
      lastTimeRef.current = video.currentTime;
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => video.removeEventListener('timeupdate', handleTimeUpdate);
  }, [playSoundOnce, effectiveMuted, currentUrl]);

  // Unmuted autoplay can be blocked (Safari; Chrome once the user activation
  // expires), e.g. on a src swap or remount while unmuted. The animation must
  // never freeze because of sound: retry play() and fall back to muted
  // playback when the browser refuses.
  useEffect(() => {
    const video = currentVideoRef.current;
    if (!video || isPaused) return;

    video.play().catch(() => {
      if (video.muted) return;
      video.muted = true;
      video.play().catch(() => undefined);
    });
  }, [currentUrl, effectiveMuted, isPaused]);

  useEffect(() => {
    if (loopUrl && loopUrl !== prevUrlRef.current && prevUrlRef.current) {
      setNextUrl(loopUrl);
      const mediaType = getMediaType(loopUrl);

      const handleTransitionComplete = () => {
        setCurrentUrl(loopUrl);
        setNextUrl(undefined);
        setShowCurrent(true);
        setShowNext(false);
        prevUrlRef.current = loopUrl;
      };

      const handleReady = () => {
        if (mediaType === 'video') {
          syncVideoTiming(currentVideoRef.current, nextVideoRef.current);
        }
        performTransition(
          setShowNext,
          setShowCurrent,
          handleTransitionComplete,
        );
      };

      const handleError = () => {
        setCurrentUrl(loopUrl);
        setNextUrl(undefined);
        prevUrlRef.current = loopUrl;
      };

      preloadMedia(loopUrl, mediaType, handleReady, handleError);
    } else if (loopUrl && !prevUrlRef.current) {
      setCurrentUrl(loopUrl);
      prevUrlRef.current = loopUrl;
    } else if (!loopUrl) {
      setCurrentUrl(undefined);
      setNextUrl(undefined);
      prevUrlRef.current = undefined;
    }
  }, [loopUrl]);

  useEffect(() => {
    controlVideoPlayback(currentVideoRef.current, isPaused || false);
    controlVideoPlayback(nextVideoRef.current, isPaused || false);
  }, [isPaused]);

  // A paused klydo rests on the artist's chosen frame. A seek before the element
  // knows its duration is dropped, so an unloaded video waits for metadata.
  useEffect(() => {
    const video = currentVideoRef.current;
    if (!video || !isPaused || !previewFrameSeconds) return;

    const seekToPreviewFrame = () => {
      video.currentTime = previewFrameSeconds;
    };

    if (video.readyState >= HAVE_METADATA) {
      seekToPreviewFrame();
      return;
    }

    video.addEventListener('loadedmetadata', seekToPreviewFrame);
    return () =>
      video.removeEventListener('loadedmetadata', seekToPreviewFrame);
  }, [isPaused, previewFrameSeconds, currentUrl]);

  if (photoUrl) {
    return (
      <AnimationWrapper>
        <StaticImage loading="lazy" src={photoUrl} alt="" />
      </AnimationWrapper>
    );
  }

  if (!currentUrl && !nextUrl) return null;

  const renderMedia = (
    url: string | undefined,
    ref: React.RefObject<HTMLVideoElement | null>,
    isMuted: boolean,
  ) => {
    if (!url) return null;
    const mediaType = getMediaType(url);

    return mediaType === 'video' ? (
      <video
        ref={ref}
        muted={isMuted}
        src={url}
        autoPlay
        loop
        playsInline
        preload="auto"
      />
    ) : (
      <img src={url} alt="" />
    );
  };

  return (
    <AnimationWrapper>
      {currentUrl && (
        <MediaElement visible={showCurrent} zIndex={1}>
          {renderMedia(currentUrl, currentVideoRef, effectiveMuted)}
        </MediaElement>
      )}
      {nextUrl && (
        <MediaElement visible={showNext} zIndex={2}>
          {renderMedia(nextUrl, nextVideoRef, true)}
        </MediaElement>
      )}
    </AnimationWrapper>
  );
};

export default KlydoAnimation;
